// pages/features/ReferralCoordinationFeaturePage.jsx
import React from "react";
import { Link } from "react-router-dom";
import {
  FileText,
  Search,
  CheckCircle,
  CalendarCheck,
  BadgeCheck,
} from "lucide-react";
import HomeHeader from "../modals/HomeHeader.js";
import physicianReferralImage from "../assets/illustrations/physician_referral.png";

const EXPLORE_PATH = "/ProviderSearchPage";

const PIPELINE_STEPS = [
  {
    icon: FileText,
    label: "Referral Request",
    status: "Submitted",
    statusColor: "bg-teal-100 text-teal-700",
  },
  {
    icon: Search,
    label: "Specialist Match",
    status: "In Progress",
    statusColor: "bg-teal-50 text-teal-700 border border-teal-200",
  },
  {
    icon: CheckCircle,
    label: "Acceptance",
    status: "Pending",
    statusColor: "bg-gray-100 text-gray-500",
  },
  {
    icon: CalendarCheck,
    label: "Appointment Scheduling",
    status: "Pending",
    statusColor: "bg-gray-100 text-gray-500",
  },
  {
    icon: BadgeCheck,
    label: "Confirmed Completion",
    status: "Pending",
    statusColor: "bg-gray-100 text-gray-500",
  },
];

export default function ReferralCoordinationFeaturePage() {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <HomeHeader />

      {/* HERO */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
              Referral Coordination
            </h1>

            <p className="mt-3 text-lg text-gray-600 leading-relaxed">
              Replace fragmented calls and faxes with structured referral
              intake, smart specialist matching, and real-time visibility into
              referral status—so care handoffs happen without gaps.
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              <Pill label="Specialty" />
              <Pill label="Scheduling" accent="emerald" />
              <Pill label="Status" />
              <Pill label="Matching" accent="emerald" />
              <Pill label="Structured" accent="teal" />
            </div>

            <div className="mt-8">
              <Link
                to={EXPLORE_PATH}
                className="inline-flex items-center justify-center bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-full px-6 py-3 text-sm transition-colors"
              >
                Explore how MedMaze works →
              </Link>
              <p className="mt-2 text-xs text-gray-500">
                See referral coordination in the live experience.
              </p>
            </div>
          </div>

          {/* Referral Pipeline Illustration */}
          <div className="rounded-2xl border-2 border-teal-400 bg-white shadow-lg overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="font-semibold text-gray-800">
                Referral Pipeline
              </div>
              <div className="text-xs text-teal-600 font-semibold">
                Active referral
              </div>
            </div>

            <div className="p-4 space-y-2">
              {PIPELINE_STEPS.map((step, i) => {
                const StepIcon = step.icon;
                return (
                  <div
                    key={i}
                    className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${
                      i === 1
                        ? "border-teal-400 bg-teal-50/60"
                        : "border-gray-200 bg-white"
                    }`}
                  >
                    <div
                      className={`h-8 w-8 shrink-0 rounded-lg flex items-center justify-center ${
                        i <= 1 ? "bg-teal-100" : "bg-gray-100"
                      }`}
                    >
                      <StepIcon
                        size={16}
                        strokeWidth={1.8}
                        color={i <= 1 ? "#0D9488" : "#9CA3AF"}
                      />
                    </div>
                    <span
                      className={`flex-1 text-sm font-medium ${
                        i < 2 ? "text-gray-900" : "text-gray-400"
                      }`}
                    >
                      {step.label}
                    </span>
                    <span
                      className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${step.statusColor}`}
                    >
                      {step.status}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* THE PROBLEM — 2-col: text left, illustration right */}
      <section className="bg-gradient-to-br from-teal-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">The Problem</h2>
            <div className="mt-4 space-y-3 text-gray-700 leading-relaxed">
              <p>
                Referrals are one of the most friction-heavy steps in care
                delivery. A primary care provider identifies a patient who needs
                specialist attention, but the path from that decision to an
                actual specialist appointment is often slow, opaque, and manual.
              </p>
              <p>
                Phone calls, faxed forms, and unstructured intake processes mean
                that referral information gets lost, delayed, or misrouted.
                Neither the referring provider nor the patient knows where
                things stand—and follow-up becomes a burden on everyone.
              </p>
              <p>
                The result: patients fall through the cracks, specialists
                receive incomplete information, and care coordination becomes a
                game of phone tag rather than a guided process.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-center">
            <img
              src={physicianReferralImage}
              alt="Physician referral illustration"
              className="w-full max-w-sm object-contain drop-shadow-md"
            />
          </div>
        </div>
      </section>

      {/* TWO-COLUMN OUTCOME SECTION */}
      <section className="bg-white py-4 pb-16">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* What MedMaze Enables */}
          <div className="rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-7 bg-gradient-to-br from-teal-50 to-white">
            <h3 className="text-xl font-bold text-gray-900">
              What MedMaze Enables
            </h3>
            <ul className="mt-4 space-y-3 text-sm text-gray-700 leading-relaxed">
              <li className="flex gap-3">
                <BulletIcon />
                <span>
                  Smart specialist matching based on patient needs, location,
                  and availability.
                </span>
              </li>
              <li className="flex gap-3">
                <BulletIcon />
                <span>
                  Structured referral intake so specialists receive complete,
                  organized information.
                </span>
              </li>
              <li className="flex gap-3">
                <BulletIcon />
                <span>
                  Faster coordination with fewer manual touchpoints between
                  referring and receiving providers.
                </span>
              </li>
              <li className="flex gap-3">
                <BulletIcon />
                <span>
                  Visibility into referral status for both patients and
                  referring providers.
                </span>
              </li>
              <li className="flex gap-3">
                <BulletIcon />
                <span>
                  Reduced administrative burden—no more chasing faxes or placing
                  follow-up calls.
                </span>
              </li>
              <li className="flex gap-3">
                <BulletIcon />
                <span>
                  Reliable scheduling handoffs so referrals convert into actual
                  appointments.
                </span>
              </li>
            </ul>
          </div>

          {/* The Vision */}
          <div className="rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-7 bg-gradient-to-br from-teal-50 to-white">
            <h3 className="text-xl font-bold text-gray-900">The Vision</h3>
            <div className="mt-4 text-sm text-gray-700 leading-relaxed space-y-3">
              <p>
                MedMaze envisions referral coordination as a guided pathway—not
                a handoff that disappears into a void. When a provider initiates
                a referral, the platform takes on the coordination work: finding
                the right specialist, confirming intake, and keeping everyone
                informed at each step.
              </p>
              <p>
                Patients shouldn't have to wonder if their referral went
                through. Referring providers shouldn't lose sight of a patient
                the moment they send a fax. Specialists shouldn't receive
                incomplete or missing information.
              </p>
              <p>
                A complete referral coordination system means that transitions
                in care are seamless, accountable, and patient-centered—from the
                first request to confirmed completion.
              </p>
            </div>
            <div className="mt-5 rounded-xl border border-teal-100 bg-teal-50 p-4">
              <p className="text-xs font-semibold text-teal-700">Goal</p>
              <p className="mt-1 text-sm text-gray-700">
                Every referral that starts in MedMaze ends with a confirmed
                appointment—not a missed connection.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHY THIS MATTERS */}
      <section className="bg-teal-50 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6 sm:p-8">
            <h3 className="text-lg font-bold text-gray-900">
              Why this matters
            </h3>
            <p className="mt-2 text-sm text-gray-700 leading-relaxed">
              Referral coordination is where care continuity is most at risk. By
              replacing fragmented, manual processes with structured intake,
              smart matching, and real-time status visibility, MedMaze ensures
              that specialist care actually reaches the patients who need it.
            </p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-white py-8 text-xs text-gray-500 text-center border-t">
        © {new Date().getFullYear()} MedMaze. All rights reserved.
      </footer>
    </div>
  );
}

function Pill({ label, accent }) {
  const base =
    "inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold";
  const theme =
    accent === "emerald"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : accent === "teal"
        ? "border-teal-300 bg-teal-50 text-teal-700"
        : "border-teal-200 bg-teal-50 text-teal-700";

  return <span className={`${base} ${theme}`}>{label}</span>;
}

function BulletIcon() {
  return (
    <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal-100 text-teal-700">
      ✓
    </span>
  );
}
